import React from 'react';
import { Box, Typography } from '@mui/material';
import { HourlyForecastTimelineSegment, HourlyFogPrediction, ThresholdResult } from '../../models/HourlyForecastTimelineData';

interface HourlyForecastTimelineDebugHoverProps {
  lastHoveredSegment: HourlyForecastTimelineSegment | null;
  formatTimeRange: (startTime: Date, isPast?: boolean) => string;
  isInPast: (time: Date, now: Date) => boolean;
  hoveredIsPast?: boolean;
  visible: boolean;
  onDismiss: () => void;
}

const HourlyForecastTimelineDebugHover: React.FC<HourlyForecastTimelineDebugHoverProps> = ({
  lastHoveredSegment,
  formatTimeRange,
  isInPast,
  hoveredIsPast,
  visible,
  onDismiss
}) => {
  if (!visible) return null;

  // Helper to format trend with arrow
  const formatTrend = (trend: 'up' | 'down' | 'neutral' | undefined, slope: number | null | undefined): string => {
    if (!trend) return 'N/A';
    const arrow = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';
    const slopeStr = slope !== null && slope !== undefined ? ` (${slope.toFixed(4)})` : '';
    return `${arrow} ${trend}${slopeStr}`;
  };

  // Helper to get color for visibility level
  const getVisibilityColor = (level: number | null | undefined): string => {
    if (level === null || level === undefined) return 'inherit';
    if (level <= 800) return '#ef5350'; // Red
    if (level <= 1600) return '#ff9800'; // Orange
    if (level <= 3000) return '#ffd54f'; // Yellow
    return '#4caf50'; // Green
  };

  // Group predictions by visibility threshold
  const groupPredictionsByThreshold = (predictions: HourlyFogPrediction[]): Map<number, HourlyFogPrediction[]> => {
    const groups = new Map<number, HourlyFogPrediction[]>();
    for (const pred of predictions) {
      const threshold = pred.visibility_threshold;
      if (!groups.has(threshold)) {
        groups.set(threshold, []);
      }
      groups.get(threshold)!.push(pred);
    }
    return groups;
  };

  // Check if a prediction ID is in the used list for its threshold
  const isPredictionUsed = (predId: number | undefined, thresholdResults: ThresholdResult[] | undefined, visibilityThreshold: number): boolean => {
    if (!predId || !thresholdResults) return false;
    const thresholdResult = thresholdResults.find(tr => tr.visibility_threshold === visibilityThreshold);
    if (!thresholdResult) return false;
    return thresholdResult.predictions_used.includes(predId);
  };

  // Check if an actual falls within the last 10 minutes of its bucket (used for hmean calculation)
  const ACTUALS_WINDOW_MINUTES = 10;
  const isActualInWindow = (forecastTime: string, bucketEnd: string | undefined): boolean => {
    if (!bucketEnd) return false;
    const actualTime = new Date(forecastTime).getTime();
    const endTime = new Date(bucketEnd).getTime();
    const windowCutoff = endTime - ACTUALS_WINDOW_MINUTES * 60 * 1000;
    return actualTime >= windowCutoff;
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        overflowX: 'auto',
        position: 'relative'
      }}
    >
      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
        <Typography
          component="button"
          onClick={onDismiss}
          sx={{
            background: 'none',
            border: 'none',
            color: '#aaa',
            fontSize: '0.9rem',
            cursor: 'pointer',
            p: 0,
            m: 0,
            '&:hover': {
              color: '#fff'
            }
          }}
        >
          Dismiss
        </Typography>
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, color: '#e0e0e0' }}>
        {lastHoveredSegment
          ? `Segment Data (${formatTimeRange(new Date(lastHoveredSegment.time), hoveredIsPast ?? isInPast(new Date(lastHoveredSegment.time), new Date()))})`
          : 'Hover over a timeline segment to see its data'}
      </Typography>

      {lastHoveredSegment ? (
        <>
          {/* PREDICTIONS - for future segments */}
          {!(hoveredIsPast ?? isInPast(new Date(lastHoveredSegment.time), new Date())) && lastHoveredSegment.raw_predictions && (
            <>
              {/* Overall Summary */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#aaa' }}>
                  Overall Result
                </Typography>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', marginTop: '4px' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '4px 8px', color: '#aaa', width: '180px' }}>Fog Predicted:</td>
                      <td style={{
                        padding: '4px 8px',
                        fontWeight: 'bold',
                        color: lastHoveredSegment.raw_predictions.fog_predicted ? '#ef5350' : '#4caf50'
                      }}>
                        {lastHoveredSegment.raw_predictions.fog_predicted ? 'Yes' : 'No'}
                      </td>
                      <td style={{ padding: '4px 8px', color: '#aaa', width: '180px' }}>Displayed Visibility Level:</td>
                      <td style={{
                        padding: '4px 8px',
                        fontWeight: 'bold',
                        color: getVisibilityColor(lastHoveredSegment.raw_predictions.lowest_visibility_level)
                      }}>
                        {lastHoveredSegment.raw_predictions.lowest_visibility_level ?? 'N/A'}
                        <span style={{ marginLeft: '4px', fontSize: '0.75rem', color: '#64b5f6' }}>(worst threshold with fog)</span>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '4px 8px', color: '#aaa' }}>Trend:</td>
                      <td style={{ padding: '4px 8px' }} colSpan={3}>
                        {formatTrend(lastHoveredSegment.raw_predictions.trend, lastHoveredSegment.raw_predictions.trend_slope)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Box>

              {/* Per-Threshold Results Summary */}
              {lastHoveredSegment.raw_predictions.threshold_results && lastHoveredSegment.raw_predictions.threshold_results.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#aaa' }}>
                    Per-Threshold Results (top 15 predictions used per threshold)
                  </Typography>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', marginTop: '4px' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Visibility Threshold</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Fog Predicted</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Confidence</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Pred Threshold</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Predictions</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Num Used in Calc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastHoveredSegment.raw_predictions.threshold_results
                        .slice()
                        .sort((a, b) => a.visibility_threshold - b.visibility_threshold)
                        .map((tr, idx) => {
                          const isDisplayedThreshold = tr.visibility_threshold === lastHoveredSegment.raw_predictions?.lowest_visibility_level;
                          return (
                            <tr key={idx} style={{
                              backgroundColor: isDisplayedThreshold ? 'rgba(76, 175, 80, 0.2)' : 'transparent',
                              fontWeight: isDisplayedThreshold ? 'bold' : 'normal'
                            }}>
                              <td style={{
                                padding: '4px 8px',
                                color: getVisibilityColor(tr.visibility_threshold)
                              }}>
                                {tr.visibility_threshold}m
                                {isDisplayedThreshold && <span style={{ marginLeft: '4px', color: '#64b5f6', fontSize: '0.75rem' }}>← DISPLAYED</span>}
                              </td>
                              <td style={{
                                padding: '4px 8px',
                                color: tr.fog_predicted ? '#ef5350' : '#4caf50'
                              }}>
                                {tr.fog_predicted ? 'Yes' : 'No'}
                              </td>
                              <td style={{ padding: '4px 8px' }}>
                                {tr.confidence?.toFixed(4) ?? 'N/A'}
                              </td>
                              <td style={{ padding: '4px 8px' }}>
                                {tr.prediction_threshold?.toFixed(4) ?? 'N/A'}
                              </td>
                              <td style={{ padding: '4px 8px' }}>
                                {tr.prediction_count}
                              </td>
                              <td style={{ padding: '4px 8px' }}>
                                {tr.evaluation_count}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </Box>
              )}

              {/* Individual Predictions Grouped by Threshold */}
              {lastHoveredSegment.raw_predictions.predictions && lastHoveredSegment.raw_predictions.predictions.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#aaa' }}>
                    All Predictions by Threshold ({lastHoveredSegment.raw_predictions.predictions.length} total)
                  </Typography>

                  {Array.from(groupPredictionsByThreshold(lastHoveredSegment.raw_predictions.predictions))
                    .sort(([a], [b]) => a - b)
                    .map(([threshold, predictions]) => {
                      const thresholdResult = lastHoveredSegment.raw_predictions?.threshold_results?.find(
                        tr => tr.visibility_threshold === threshold
                      );
                      const isDisplayedThreshold = threshold === lastHoveredSegment.raw_predictions?.lowest_visibility_level;

                      return (
                        <Box key={threshold} sx={{ mt: 2, mb: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 'bold',
                              color: getVisibilityColor(threshold),
                              display: 'block',
                              mb: 0.5
                            }}
                          >
                            {threshold}m Threshold
                            {isDisplayedThreshold && (
                              <span style={{ marginLeft: '8px', color: '#64b5f6', fontWeight: 'normal' }}>
                                ← Used for display (worst with fog predicted)
                              </span>
                            )}
                            {thresholdResult && (
                              <span style={{ marginLeft: '8px', color: '#aaa', fontWeight: 'normal' }}>
                                | Fog: {thresholdResult.fog_predicted ? 'Yes' : 'No'} | Conf: {thresholdResult.confidence.toFixed(4)}
                              </span>
                            )}
                          </Typography>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>#</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>ID</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Predicted At</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Fog</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Confidence</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Pred Threshold</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Vis Threshold</th>
                              </tr>
                            </thead>
                            <tbody>
                              {predictions
                                .slice()
                                .sort((a, b) => new Date(b.predicted_at).getTime() - new Date(a.predicted_at).getTime())
                                .map((pred, idx) => {
                                  const isUsed = isPredictionUsed(
                                    pred.id,
                                    lastHoveredSegment.raw_predictions?.threshold_results,
                                    threshold
                                  );

                                  return (
                                    <tr key={idx} style={{
                                      backgroundColor: isUsed
                                        ? 'rgba(76, 175, 80, 0.15)'
                                        : 'transparent',
                                      opacity: isUsed ? 1 : 0.6
                                    }}>
                                      <td style={{ padding: '4px 8px', fontWeight: isUsed ? 'bold' : 'normal' }}>
                                        {idx + 1}
                                        {isUsed && <span style={{ color: '#4caf50', marginLeft: '4px', fontSize: '0.75rem' }}>✓</span>}
                                      </td>
                                      <td style={{ padding: '4px 8px' }}>{pred.id || 'N/A'}</td>
                                      <td style={{ padding: '4px 8px' }}>{new Date(pred.predicted_at).toISOString()}</td>
                                      <td style={{
                                        padding: '4px 8px',
                                        color: pred.fog_predicted ? '#ef5350' : '#4caf50'
                                      }}>
                                        {pred.fog_predicted ? 'Yes' : 'No'}
                                      </td>
                                      <td style={{ padding: '4px 8px' }}>
                                        {pred.fog_prediction_confidence?.toFixed(4) ?? 'N/A'}
                                      </td>
                                      <td style={{ padding: '4px 8px' }}>
                                        {pred.prediction_threshold?.toFixed(4) ?? 'N/A'}
                                      </td>
                                      <td style={{
                                        padding: '4px 8px',
                                        color: getVisibilityColor(pred.visibility_threshold)
                                      }}>
                                        {pred.visibility_threshold}m
                                      </td>
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </Box>
                      );
                    })}
                </Box>
              )}
            </>
          )}

          {/* ACTUALS - for past segments */}
          {(hoveredIsPast ?? isInPast(new Date(lastHoveredSegment.time), new Date())) && lastHoveredSegment.raw_actuals && (
            <>
              {/* Summary for Actuals */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#aaa' }}>
                  Actuals Summary <span style={{ fontWeight: 'normal' }}>(metrics use last {ACTUALS_WINDOW_MINUTES} min only)</span>
                </Typography>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', marginTop: '4px' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: '4px 8px', color: '#aaa', width: '180px' }}>Fog Observed:</td>
                      <td style={{
                        padding: '4px 8px',
                        fontWeight: 'bold',
                        color: lastHoveredSegment.raw_actuals.fog_predicted ? '#ef5350' : '#4caf50'
                      }}>
                        {lastHoveredSegment.raw_actuals.fog_predicted ? 'Yes' : 'No'}
                      </td>
                      <td style={{ padding: '4px 8px', color: '#aaa', width: '180px' }}>Actual Count:</td>
                      <td style={{ padding: '4px 8px' }}>
                        {lastHoveredSegment.raw_actuals.actual_count ?? 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '4px 8px', color: '#aaa' }}>Visibility Harmonic Mean:</td>
                      <td style={{
                        padding: '4px 8px',
                        fontWeight: 'bold',
                        color: getVisibilityColor(lastHoveredSegment.raw_actuals.visibility_hmean)
                      }}>
                        {lastHoveredSegment.raw_actuals.visibility_hmean?.toFixed(0) ?? 'N/A'}
                        <span style={{ marginLeft: '4px', fontSize: '0.75rem', color: '#64b5f6' }}>(displayed)</span>
                      </td>
                      <td style={{ padding: '4px 8px', color: '#aaa' }}>Visibility Mean:</td>
                      <td style={{
                        padding: '4px 8px',
                        color: getVisibilityColor(lastHoveredSegment.raw_actuals.visibility_mean)
                      }}>
                        {lastHoveredSegment.raw_actuals.visibility_mean?.toFixed(0) ?? 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '4px 8px', color: '#aaa' }}>Visibility Median:</td>
                      <td style={{
                        padding: '4px 8px',
                        color: getVisibilityColor(lastHoveredSegment.raw_actuals.visibility_median)
                      }}>
                        {lastHoveredSegment.raw_actuals.visibility_median ?? 'N/A'}
                      </td>
                      <td style={{ padding: '4px 8px', color: '#aaa' }}>Range:</td>
                      <td style={{ padding: '4px 8px' }}>
                        <span style={{ color: getVisibilityColor(lastHoveredSegment.raw_actuals.lowest_visibility_level) }}>
                          {lastHoveredSegment.raw_actuals.lowest_visibility_level ?? 'N/A'}
                        </span>
                        {' - '}
                        <span style={{ color: getVisibilityColor(lastHoveredSegment.raw_actuals.highest_visibility_level) }}>
                          {lastHoveredSegment.raw_actuals.highest_visibility_level ?? 'N/A'}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Box>

              {/* Individual Fog Actuals Table */}
              {lastHoveredSegment.raw_actuals.fog_actuals && lastHoveredSegment.raw_actuals.fog_actuals.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#aaa' }}>
                    Fog Actuals ({lastHoveredSegment.raw_actuals.fog_actuals.length} observations)
                    <span style={{ fontWeight: 'normal', marginLeft: '8px', color: '#4caf50' }}>
                      ✓ = used in hmean (last {ACTUALS_WINDOW_MINUTES} min)
                    </span>
                  </Typography>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem', marginTop: '4px' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>#</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>ID</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Forecast Time</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Visibility Level</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #444' }}>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lastHoveredSegment.raw_actuals.fog_actuals
                        .slice()
                        .sort((a, b) => new Date(b.forecast_time).getTime() - new Date(a.forecast_time).getTime())
                        .map((actual, idx) => {
                          const inWindow = isActualInWindow(actual.forecast_time, lastHoveredSegment.raw_actuals?.bucket_end);
                          return (
                            <tr key={idx} style={{
                              backgroundColor: inWindow
                                ? 'rgba(76, 175, 80, 0.15)'
                                : 'transparent',
                              opacity: inWindow ? 1 : 0.6
                            }}>
                              <td style={{ padding: '4px 8px', fontWeight: inWindow ? 'bold' : 'normal' }}>
                                {idx + 1}
                                {inWindow && <span style={{ color: '#4caf50', marginLeft: '4px', fontSize: '0.75rem' }}>✓</span>}
                              </td>
                              <td style={{ padding: '4px 8px' }}>{actual.id || 'N/A'}</td>
                              <td style={{ padding: '4px 8px' }}>{new Date(actual.forecast_time).toISOString()}</td>
                              <td style={{
                                padding: '4px 8px',
                                color: getVisibilityColor(actual.visibility_level)
                              }}>
                                {actual.visibility_level}
                              </td>
                              <td style={{ padding: '4px 8px' }}>{actual.source}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </Box>
              )}
            </>
          )}

          {/* No data message */}
          {!lastHoveredSegment.raw_predictions && !lastHoveredSegment.raw_actuals && (
            <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
              No raw data available for this segment
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
          No segment selected
        </Typography>
      )}
    </Box>
  );
};

export default HourlyForecastTimelineDebugHover;
